import React, { memo, useEffect, useMemo, useRef, useState, type FunctionComponent } from "react";
import { CheckOutlined, FilterOutlined, LoadingOutlined, MoreOutlined } from "@ant-design/icons";
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Drawer,
  Dropdown,
  Empty,
  List,
  message,
  Space,
  Tabs,
  type DropdownProps,
  type MenuProps,
  type TabsProps,
} from "antd";
import { useTranslation } from "react-i18next";

import { NotificationAction, notificationsActions, RequestStatus } from "@fitness-app/app-store";
import { PermissionKey, PermissionScope } from "@fitness-app/data-models";
import {
  type ClientNotifications,
  type NotificationType,
  type OwnerNotifications,
  type TrainerNotifications,
} from "@fitness-app/data-models/entities/Notification";

import { usePermission } from "~/hooks/usePermission";
import NotificationItem from "~/modules/Dashboard/NotificationsDrawer/NotificationItem";
import {
  getDefaultNotificationFilter,
  getNotificationFilterOptions,
  type NotificationFilterOption,
} from "~/modules/Dashboard/NotificationsDrawer/notificationFilters";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

interface OwnProps {
  isOpen: boolean;
  toggleDrawer: () => void;
}

type Props = OwnProps;

// eslint-disable-next-line react/display-name
const NotificationFilter = memo(
  ({
    options,
    selectedKeys,
    setSelectedKeys,
  }: {
    options: NotificationFilterOption[];
    selectedKeys: NotificationType[];
    setSelectedKeys: (keys: NotificationType[]) => void;
  }) => {
    const [open, setOpen] = useState(false);
    const { t } = useTranslation(["dashboard", "common"]);

    const filters = options.map(({ type }) => ({
      key: type,
      label: (
        <Checkbox onClick={(ev) => ev.stopPropagation()} checked={selectedKeys.includes(type)}>
          {t(`notificationTypeTitle.${type}`)}
        </Checkbox>
      ),
    }));

    const handleOpenChange: DropdownProps["onOpenChange"] = (nextOpen, info) => {
      if (info.source === "trigger" || nextOpen) {
        setOpen(nextOpen);
      }
    };

    return (
      <Space size={4}>
        {selectedKeys.length > 0 && (
          <Button onClick={() => setSelectedKeys([])} type="link">
            {t("common:button.reset")}
          </Button>
        )}
        <Dropdown
          onOpenChange={handleOpenChange}
          open={open}
          menu={{
            items: filters,
            selectable: true,
            selectedKeys,
            multiple: true,
            onDeselect: ({ selectedKeys }) => {
              setSelectedKeys(selectedKeys as NotificationType[]);
            },
            onSelect: ({ selectedKeys }) => {
              setSelectedKeys(selectedKeys as NotificationType[]);
            },
          }}
        >
          <Button type={selectedKeys.length ? "primary" : undefined} icon={<FilterOutlined />} />
        </Dropdown>
      </Space>
    );
  },
);

const NotificationsDrawer: FunctionComponent<Props> = ({ toggleDrawer, isOpen }) => {
  const { t } = useTranslation(["dashboard", "common"]);
  const dispatch = useAppDispatch();
  const receivesPurchaseNotifications = usePermission(PermissionKey.PRODUCTS_MANAGE, PermissionScope.ALL);
  const filterOptions = useMemo(
    () => getNotificationFilterOptions(receivesPurchaseNotifications),
    [receivesPurchaseNotifications],
  );
  const [selectedKeys, setSelectedKeys] = useState<NotificationType[]>(() =>
    getDefaultNotificationFilter(receivesPurchaseNotifications),
  );
  const presetAppliedFor = useRef(receivesPurchaseNotifications);
  const [activeTab, setActiveTab] = React.useState<"unread" | "all">("unread");
  const { totalUnread, listStatus, notifications, page, hasMore, performingAction } = useAppSelector(
    (store) => store.notifications,
  );
  const [fetchingNextPage, setFetchingNextPage] = useState(false);

  // Permissions arrive with the user profile, so the drawer can mount before this one is known.
  // Without the resync the owner would keep the empty (= unfiltered) preset for the whole session,
  // and the badge — now counted over the same filter — would be back to promising notifications the
  // list never shows.
  useEffect(() => {
    if (presetAppliedFor.current !== receivesPurchaseNotifications) {
      presetAppliedFor.current = receivesPurchaseNotifications;
      setSelectedKeys(getDefaultNotificationFilter(receivesPurchaseNotifications));
    }
  }, [receivesPurchaseNotifications]);

  useEffect(() => {
    void dispatch(notificationsActions.subscribeToNotifications(activeTab, selectedKeys));

    return () => {
      void dispatch(notificationsActions.unsubscribeFromNotifications());
    };
  }, [dispatch, activeTab, selectedKeys]);

  const onLoadMore = async () => {
    setFetchingNextPage(true);
    await dispatch(notificationsActions.fetchNotifications({ page: page + 1, type: selectedKeys, filter: activeTab }));
    setFetchingNextPage(false);
  };

  const renderContent = () => {
    if (listStatus === RequestStatus.FAILED) {
      return (
        <div className="p-4">
          <Alert type="error" showIcon message={t("errors.cannotFetchNotificationsList")} />
        </div>
      );
    }

    const loadMore =
      (listStatus === RequestStatus.SUCCESS || listStatus === RequestStatus.SUBSCRIBED) && hasMore ? (
        <div
          style={{
            textAlign: "center",
            marginTop: 12,
            marginBottom: 12,
            height: 32,
            lineHeight: "32px",
          }}
        >
          <Button onClick={onLoadMore} loading={fetchingNextPage}>
            Pobierz więcej
          </Button>
        </div>
      ) : null;

    return (
      <List
        itemLayout="horizontal"
        dataSource={notifications as (ClientNotifications | OwnerNotifications | TrainerNotifications)[]}
        loading={listStatus === RequestStatus.FETCHING || listStatus === RequestStatus.SUBSCRIBING}
        renderItem={(notification) => <NotificationItem notification={notification} closeDrawer={toggleDrawer} />}
        loadMore={loadMore}
        locale={{
          emptyText: (
            <Empty
              className="mt-12"
              description={t(activeTab === "unread" ? "notificationsDrawer.emptyUnread" : "notificationsDrawer.empty")}
            />
          ),
        }}
      />
    );
  };

  const items: TabsProps["items"] = [
    {
      key: "unread",
      label: (
        <Space>
          {t("notificationsDrawer.unread")}
          <Badge count={totalUnread} overflowCount={99} />
        </Space>
      ),
      children: renderContent(),
    },
    {
      key: "all",
      label: <span>{t("notificationsDrawer.all")}</span>,
      children: renderContent(),
    },
  ];

  const markingAllAsRead = performingAction?.action === NotificationAction.MarkAsRead;

  const dropdown: MenuProps["items"] = useMemo(
    () => [
      {
        key: "1",
        label: t("notificationsDrawer.markAllAsRead"),
        icon: markingAllAsRead ? <LoadingOutlined /> : <CheckOutlined />,
        disabled: markingAllAsRead,
        onClick: async () => {
          const result = await dispatch(notificationsActions.markAllAsRead({ type: selectedKeys }));

          // TRE-1823: a bulk mark used to fail silently and leave the badge lying about a clean
          // inbox — say it out loud instead, the list has already been re-read at this point.
          if (notificationsActions.markAllAsRead.rejected.match(result)) {
            void message.error(t("errors.cannotMarkAllNotificationsAsRead"));
            return;
          }

          if (!result.payload.completed) {
            void message.warning(t("notificationsDrawer.markAllAsReadPartial"));
          }
        },
      },
    ],
    [t, dispatch, selectedKeys, markingAllAsRead],
  );

  return (
    <Drawer
      title={t("notificationsDrawer.title")}
      extra={
        totalUnread > 0 && (
          <Dropdown menu={{ items: dropdown }}>
            <Button type="text">
              <Space>
                <MoreOutlined className="rotate-90 text-xl" />
              </Space>
            </Button>
          </Dropdown>
        )
      }
      placement="right"
      onClose={toggleDrawer}
      open={isOpen}
      width={450}
      bodyStyle={{ padding: 0, paddingTop: 0, height: "auto" }}
      className="drawer max-w-full"
    >
      <Tabs
        tabBarStyle={{
          padding: "0 16px",
          marginBottom: 0,
          backgroundColor: "white",
        }}
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "unread" | "all")}
        items={items}
        tabBarExtraContent={
          <NotificationFilter options={filterOptions} selectedKeys={selectedKeys} setSelectedKeys={setSelectedKeys} />
        }
      />
    </Drawer>
  );
};

export default NotificationsDrawer;
